import React, { useState } from 'react';
import { XIcon, DownloadIcon, CheckSquareIcon, SquareIcon } from 'lucide-react';
import { sampleSuppliers, sampleOutreach } from '../../data/suppliers';
import { SUPPLIER_CSV_HEADERS, BUYER_REQUEST_CSV_HEADERS, OUTREACH_CSV_HEADERS } from '../../data/schema';
import { toCSV, downloadCSV } from '../../lib/csv';
const dataSources = ['API Composite List', 'Rigzone Directory', 'EPA Natural Gas STAR Provider Directory', 'Thomasnet supplier categories', 'GlobalSpec industrial product search', 'LinkedIn Sales Navigator', 'Houston oilfield equipment suppliers', 'Louisiana oil and gas association/member directories', 'Texas/Louisiana industrial distributors', 'Existing export/logistics companies serving Latin America'];
const categoryRoadmap = ['15 valve, choke, manifold, and flow control companies', '10 pump and compressor companies', '10 generator, transformer, and electrical infrastructure companies', '10 automation, monitoring, and industrial controls companies', '10 methane detection and environmental technology companies', '10 refinery maintenance and repair companies', '10 logistics, freight forwarding, and customs support companies', '10 API or ISO certified equipment manufacturers', '5 industrial safety equipment companies', '5 inspection, testing, and certification companies', '5 Spanish speaking or Latin America export ready companies'];
const verificationItems = ['Confirm company website', 'Confirm product category', 'Confirm certifications', 'Confirm export readiness', 'Confirm no obvious sanctions concern', 'Confirm contact person', 'Confirm willingness to discuss Venezuela-related opportunities subject to legal review', 'Confirm product catalogs', 'Confirm warranty/support capability', 'Confirm documentation availability', 'Confirm Spanish support or translation need'];
const pricingNotes = [{
  label: 'Basic Supplier Listing',
  value: '$99 / month'
}, {
  label: 'Premium Supplier Profile',
  value: '$299 / month'
}, {
  label: 'Qualified Introduction',
  value: '$500 – $2,500'
}, {
  label: 'Sourcing Support Package',
  value: '$3,000 – $10,000'
}, {
  label: 'Enterprise Trade Desk Retainer',
  value: 'Custom'
}];
interface Props {
  onClose: () => void;
}
export function SupplierBuildPlan({
  onClose
}: Props) {
  const [checked, setChecked] = useState<Record<number, boolean>>({});
  const toggle = (i: number) => setChecked(p => ({
    ...p,
    [i]: !p[i]
  }));
  const exportSuppliers = () => downloadCSV('suppliers.csv', toCSV(sampleSuppliers, SUPPLIER_CSV_HEADERS));
  const exportBuyers = () => downloadCSV('buyer_requests.csv', toCSV([], BUYER_REQUEST_CSV_HEADERS));
  const exportOutreach = () => downloadCSV('outreach.csv', toCSV(sampleOutreach, OUTREACH_CSV_HEADERS));
  return <div className="fixed inset-0 z-[100] bg-navy-deep/95 backdrop-blur-md overflow-y-auto" role="dialog" aria-modal="true" aria-label="Internal Supplier Build Plan" data-id="element-23">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-10" data-id="element-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 sticky top-0 bg-navy-deep/95 backdrop-blur-md py-4 z-10" data-id="element-25">
          <div data-id="element-26">
            <p className="font-mono text-[10.5px] tracking-[0.25em] uppercase text-gold mb-1" data-id="element-27">
              Internal · Admin View
            </p>
            <h2 className="font-serif text-2xl md:text-3xl text-white" data-id="element-28">
              Supplier Build Plan
            </h2>
          </div>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center border border-white/20 rounded-sm text-white hover:bg-white/5" aria-label="Close admin view" data-id="element-29">
            <XIcon className="w-4 h-4" data-id="element-30" />
          </button>
        </div>

        {/* Progress */}
        <div className="border border-white/10 bg-navy-800/40 rounded-md p-6 mb-6" data-id="element-31">
          <p className="font-mono text-[10.5px] tracking-wider text-gold mb-4" data-id="element-32">
            PROGRESS TRACKER
          </p>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4" data-id="element-33">
            {[{
            label: 'Target',
            value: 100,
            gold: true
          }, {
            label: 'Verified',
            value: 0
          }, {
            label: 'In Research',
            value: 0
          }, {
            label: 'Contacted',
            value: 0
          }, {
            label: 'Interested',
            value: 0
          }, {
            label: 'Approved',
            value: 0
          }].map(m => <div key={m.label} data-id="element-34">
                <div className={`font-serif text-3xl ${m.gold ? 'text-gold' : 'text-white'}`} data-id="element-35">
                  {m.value}
                </div>
                <div className="font-mono text-[10px] text-slate-400 uppercase tracking-wider mt-1" data-id="element-36">
                  {m.label}
                </div>
              </div>)}
          </div>
        </div>

        {/* Two columns */}
        <div className="grid lg:grid-cols-2 gap-5 mb-6" data-id="element-37">
          <div className="border border-white/10 bg-navy-800/40 rounded-md p-6" data-id="element-38">
            <h3 className="font-serif text-lg text-white mb-4" data-id="element-39">
              First 100 Roadmap
            </h3>
            <ul className="space-y-2 text-[13.5px] text-slate-300" data-id="element-40">
              {categoryRoadmap.map(c => <li key={c} className="flex items-start gap-3 py-1.5 border-b border-white/5 last:border-0" data-id="element-41">
                  <span className="text-gold font-mono text-[11px] mt-0.5" data-id="element-42">
                    ›
                  </span>
                  <span data-id="element-43">{c}</span>
                </li>)}
            </ul>
          </div>

          <div className="border border-white/10 bg-navy-800/40 rounded-md p-6" data-id="element-44">
            <h3 className="font-serif text-lg text-white mb-4" data-id="element-45">
              Suggested Data Sources
            </h3>
            <ul className="space-y-2 text-[13.5px] text-slate-300" data-id="element-46">
              {dataSources.map((s, i) => <li key={s} className="flex items-start gap-3 py-1.5 border-b border-white/5 last:border-0" data-id="element-47">
                  <span className="font-mono text-[11px] text-slate-500 w-6" data-id="element-48">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span data-id="element-49">{s}</span>
                </li>)}
            </ul>
          </div>
        </div>

        {/* Verification checklist */}
        <div className="border border-white/10 bg-navy-800/40 rounded-md p-6 mb-6" data-id="element-50">
          <h3 className="font-serif text-lg text-white mb-4" data-id="element-51">
            Verification Checklist
          </h3>
          <ul className="grid md:grid-cols-2 gap-2" data-id="element-52">
            {verificationItems.map((v, i) => {
            const on = !!checked[i];
            return <li key={v} data-id="element-53">
                  <button onClick={() => toggle(i)} className={`w-full flex items-start gap-3 px-3.5 py-3 border rounded-sm text-left transition-colors ${on ? 'border-gold/40 bg-gold/5' : 'border-white/10 hover:border-white/20'}`} data-id="element-54">
                    {on ? <CheckSquareIcon className="w-4 h-4 text-gold mt-0.5 shrink-0" data-id="element-55" /> : <SquareIcon className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" data-id="element-56" />}
                    <span className={`text-[13px] ${on ? 'text-white' : 'text-slate-300'}`} data-id="element-57">
                      {v}
                    </span>
                  </button>
                </li>;
          })}
          </ul>
        </div>

        {/* Outreach tracker table */}
        <div className="border border-white/10 bg-navy-800/40 rounded-md p-6 mb-6" data-id="element-58">
          <div className="flex items-center justify-between mb-4" data-id="element-59">
            <h3 className="font-serif text-lg text-white" data-id="element-60">
              Outreach Tracker (Sample)
            </h3>
            <span className="font-mono text-[10.5px] text-slate-400" data-id="element-61">
              {sampleOutreach.length} ROWS
            </span>
          </div>
          <div className="overflow-x-auto thin-scroll" data-id="element-62">
            <table className="w-full text-[12.5px] text-slate-300 min-w-[700px]" data-id="element-63">
              <thead data-id="element-64">
                <tr className="text-left border-b border-white/10" data-id="element-65">
                  <th className="py-2 pr-4 font-mono text-[10.5px] text-slate-400 uppercase tracking-wider" data-id="element-66">
                    Supplier
                  </th>
                  <th className="py-2 pr-4 font-mono text-[10.5px] text-slate-400 uppercase tracking-wider" data-id="element-67">
                    Source
                  </th>
                  <th className="py-2 pr-4 font-mono text-[10.5px] text-slate-400 uppercase tracking-wider" data-id="element-68">
                    Category
                  </th>
                  <th className="py-2 pr-4 font-mono text-[10.5px] text-slate-400 uppercase tracking-wider" data-id="element-69">
                    Status
                  </th>
                  <th className="py-2 pr-4 font-mono text-[10.5px] text-slate-400 uppercase tracking-wider" data-id="element-70">
                    Interested
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-71">
                {sampleOutreach.map(r => <tr key={r.supplier_id} className="border-b border-white/5" data-id="element-72">
                    <td className="py-3 pr-4 text-white" data-id="element-73">{r.company_name}</td>
                    <td className="py-3 pr-4" data-id="element-74">{r.source}</td>
                    <td className="py-3 pr-4" data-id="element-75">{r.category}</td>
                    <td className="py-3 pr-4" data-id="element-76">
                      <span className="px-2 py-0.5 text-[10.5px] font-mono tracking-wider uppercase border border-gold/30 text-gold rounded-sm" data-id="element-77">
                        {r.status}
                      </span>
                    </td>
                    <td className="py-3 pr-4" data-id="element-78">{r.interested ? 'Yes' : '—'}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pricing + exports */}
        <div className="grid lg:grid-cols-2 gap-5" data-id="element-79">
          <div className="border border-white/10 bg-navy-800/40 rounded-md p-6" data-id="element-80">
            <h3 className="font-serif text-lg text-white mb-4" data-id="element-81">
              Internal Pricing Notes
            </h3>
            <ul className="divide-y divide-white/10" data-id="element-82">
              {pricingNotes.map(p => <li key={p.label} className="flex items-center justify-between py-2.5" data-id="element-83">
                  <span className="text-[13.5px] text-slate-300" data-id="element-84">
                    {p.label}
                  </span>
                  <span className="font-mono text-[12.5px] text-gold" data-id="element-85">
                    {p.value}
                  </span>
                </li>)}
            </ul>
            <p className="mt-4 text-[11px] text-slate-500 leading-relaxed" data-id="element-86">
              Pricing is internal only. Do not display on public-facing pages
              without partner approval.
            </p>
          </div>

          <div className="border border-white/10 bg-navy-800/40 rounded-md p-6" data-id="element-87">
            <h3 className="font-serif text-lg text-white mb-4" data-id="element-88">CSV Exports</h3>
            <div className="space-y-2.5" data-id="element-89">
              <button onClick={exportSuppliers} className="w-full flex items-center justify-between px-4 h-11 border border-white/10 hover:border-gold/40 rounded-sm text-[13px] text-slate-200 transition-colors" data-id="element-90">
                <span data-id="element-91">Suppliers.csv</span>
                <DownloadIcon className="w-4 h-4 text-gold" data-id="element-92" />
              </button>
              <button onClick={exportBuyers} className="w-full flex items-center justify-between px-4 h-11 border border-white/10 hover:border-gold/40 rounded-sm text-[13px] text-slate-200 transition-colors" data-id="element-93">
                <span data-id="element-94">Buyer_requests.csv (template)</span>
                <DownloadIcon className="w-4 h-4 text-gold" data-id="element-95" />
              </button>
              <button onClick={exportOutreach} className="w-full flex items-center justify-between px-4 h-11 border border-white/10 hover:border-gold/40 rounded-sm text-[13px] text-slate-200 transition-colors" data-id="element-96">
                <span data-id="element-97">Outreach.csv</span>
                <DownloadIcon className="w-4 h-4 text-gold" data-id="element-98" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}